import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  Building2, BookOpen, DollarSign, Star, PlusCircle, Clock,
  TrendingUp, Users, Bell, ChevronLeft, CheckCircle, XCircle
} from 'lucide-react';
import api from '../../utils/api';
import { format } from 'date-fns';

const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
        <Icon size={20} className="text-white" />
      </div>
    </div>
    <div className="text-2xl font-bold text-white mb-1">{value}</div>
    <div className="text-dark-400 text-sm">{title}</div>
    {sub && <div className="text-primary-400 text-xs mt-1">{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, notifRes] = await Promise.all([
        api.get('/users/dashboard/stats'),
        api.get('/bookings/my?limit=5&page=1'),
        api.get('/notifications')
      ]);
      setStats(statsRes.data.data);
      setRecentBookings(bookingsRes.data.data || []);
      setNotifications(notifRes.data.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  const isOwner = user?.role === 'owner' || user?.role === 'both' || user?.role === 'admin';

  const STATUS_CONFIG = {
    pending: { label: 'قيد الانتظار', color: 'text-yellow-400 bg-yellow-400/10' },
    confirmed: { label: 'مؤكد', color: 'text-primary-400 bg-primary-400/10' },
    active: { label: 'جارٍ الآن', color: 'text-blue-400 bg-blue-400/10' },
    completed: { label: 'مكتمل', color: 'text-dark-400 bg-dark-600' },
    cancelled: { label: 'ملغي', color: 'text-red-400 bg-red-400/10' },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
      {/* Welcome */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">مرحباً، {user?.full_name?.split(' ')[0]} 👋</h1>
          <p className="text-dark-400 text-sm mt-1">{format(new Date(), 'EEEE، d MMMM yyyy')}</p>
        </div>
        {isOwner && (
          <Link to="/dashboard/add-listing" className="btn-primary">
            <PlusCircle size={16} />
            إضافة إعلان
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {isOwner ? [
            { title: 'إعلاناتي', value: stats?.owner?.total_listings || 0, icon: Building2, color: 'bg-blue-600' },
            { title: 'إجمالي الحجوزات', value: stats?.owner?.total_bookings || 0, icon: BookOpen, color: 'bg-primary-600' },
            { title: 'الإيرادات', value: `${stats?.owner?.total_revenue || 0} د`, icon: DollarSign, color: 'bg-accent' },
            { title: 'حجوزات معلقة', value: stats?.owner?.pending_bookings || 0, icon: Clock, color: 'bg-orange-600', sub: 'تحتاج مراجعة' }
          ] : [
            { title: 'حجوزاتي', value: stats?.renter?.total_bookings || 0, icon: BookOpen, color: 'bg-primary-600' },
            { title: 'مكتملة', value: stats?.renter?.completed_bookings || 0, icon: CheckCircle, color: 'bg-green-700' },
            { title: 'قادمة', value: stats?.renter?.upcoming_bookings || 0, icon: Clock, color: 'bg-blue-600' },
            { title: 'المبلغ المنفق', value: `${stats?.renter?.total_spent || 0} د`, icon: DollarSign, color: 'bg-accent' }
          ].map(item => <StatCard key={item.title} {...item} />)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Bookings */}
        <div className="lg:col-span-2">
          <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">الحجوزات الأخيرة</h2>
              <Link to="/dashboard/bookings" className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1">
                عرض الكل <ChevronLeft size={14} />
              </Link>
            </div>

            {recentBookings.length === 0 ? (
              <div className="text-center py-8 text-dark-400">
                <BookOpen size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">لا توجد حجوزات بعد</p>
                <Link to="/listings" className="text-primary-400 text-sm hover:text-primary-300 mt-2 inline-block">
                  استعرض الإعلانات
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBookings.map(booking => {
                  const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
                  return (
                    <Link key={booking.id} to={`/dashboard/bookings/${booking.uuid}`}
                      className="flex items-center gap-3 p-3 bg-dark-700 hover:bg-dark-600 rounded-xl transition-colors">
                      <div className="w-10 h-10 rounded-xl bg-dark-600 overflow-hidden flex-shrink-0">
                        {booking.listing_image ? (
                          <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${booking.listing_image}`}
                            alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 size={16} className="text-dark-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{booking.listing_title}</p>
                        <p className="text-dark-400 text-xs">{booking.booking_ref}</p>
                      </div>
                      <div className="text-left">
                        <span className={`badge text-xs ${status.color}`}>{status.label}</span>
                        <p className="text-dark-400 text-xs mt-1">{booking.total_amount} د</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div>
          <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
            <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <Bell size={18} className="text-primary-400" /> الإشعارات
            </h2>

            {notifications.length === 0 ? (
              <div className="text-center py-6 text-dark-400">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">لا توجد إشعارات</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {notifications.slice(0, 8).map(notif => (
                  <div key={notif.id}
                    className={`p-3 rounded-xl text-xs transition-colors ${notif.is_read ? 'bg-dark-700' : 'bg-primary-500/10 border border-primary-500/20'}`}>
                    <p className="text-white font-medium mb-0.5">{notif.title}</p>
                    <p className="text-dark-400">{notif.message}</p>
                    <p className="text-dark-600 mt-1">{format(new Date(notif.created_at), 'dd/MM hh:mm')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5 mt-4">
            <h2 className="text-white font-bold mb-3">إجراءات سريعة</h2>
            <div className="space-y-2">
              {[
                { to: '/listings', label: 'تصفح الإعلانات', icon: Building2 },
                { to: '/dashboard/bookings', label: 'حجوزاتي', icon: BookOpen },
                { to: '/dashboard/messages', label: 'رسائلي', icon: Users },
                ...(isOwner ? [{ to: '/dashboard/add-listing', label: 'إضافة إعلان', icon: PlusCircle }] : [])
              ].map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to}
                  className="flex items-center gap-2.5 p-2.5 bg-dark-700 hover:bg-dark-600 rounded-xl text-dark-200 hover:text-white text-sm transition-colors">
                  <Icon size={15} className="text-primary-400" />{label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
